import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  DistributionList,
  ChannelType,
  ChannelUIds,
  DistributionListType
} from 'src/app/modules/communications/store/communications/communications.model';
import { cloneDeep } from 'lodash-es';
import { GUID_EMPTY } from '@app/core/constants';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { FieldDataType, SchemaField } from 'src/app/modules/core/store/models';
import { OrgBusinessUnit } from '@app/modules/core/org-business-units/org-business-unit.model';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units/org-business-units-api.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'topo-new-distribution-list',
  templateUrl: './new-distribution-list.component.html',
  styleUrls: ['./new-distribution-list.component.scss'],
  standalone: false
})
export class NewDistributionListComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;
  selectedTabIndex = 0;
  tabs: string[] = [];
  distributionList: DistributionList = {
    uId: null,
    name: null,
    description: null,
    channelType: ChannelType.Email,
    channelUId: null,
    nameFieldUId: null,
    destinationFieldUId: null,
    isActive: null,
    createdOn: null,
    updatedOn: null,
    lastEditedByName: null,
    lastEditedById: null,
    queryMatchRule: {
      uId: null,
      datasetUId: null,
      root: {
        uId: null,
        filters: null,
        filterGroups: null,
        groupType: null
      }
    },
    businessUnitUIds: null,
    distributionListType: DistributionListType.None
  };
  destroy$ = new Subject<void>();
  channelTypes: { name: string; channelType: ChannelType; channelUId?: string }[] = [];
  selectedChannelType: { name: null; channelType: ChannelType.Email; channelUId: null };
  datasets = [];
  nameFields = [];
  destinationFields = [];
  businessUnits: OrgBusinessUnit[];
  selectedUnitUIds = [];
  hasBusinessUnits: boolean;
  rootUnitUId: string;

  constructor(
    private store: Store<fromComm.State>,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    private userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromComm.loadDatasetsForSelect({ includeWorkflow: false }));

    this.tabs.push(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TAB-OVERVIEW'));
    this.tabs.push(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TAB-FILTERS'));

    this.channelTypes.push({
      name: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${ChannelType.Email}`),
      channelType: ChannelType.Email
    });
    this.channelTypes.push({
      name: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${ChannelType.Sms}`),
      channelType: ChannelType.Sms
    });
    this.channelTypes.push({
      name: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-EVERBRIDGE`),
      channelType: ChannelType.MassNotifications,
      channelUId: ChannelUIds.EverbridgeChannelUId
    });
    this.channelTypes.sort((a, b) => (a.name > b.name ? 1 : -1));

    this.orgBusinessUnitsApiService.hasOrgBusinessUnits().subscribe(hasBusinessUnits => {
      this.hasBusinessUnits = hasBusinessUnits;
    });

    this.store
      .select(fromComm.selectSelectedDistributionList)
      .pipe(withLatestFrom(this.store.select(fromComm.selectDistributionListNewIsActive)), takeUntil(this.destroy$))
      .subscribe(([distributionList, isActive]) => {
        if (distributionList) {
          this.distributionList = cloneDeep(distributionList);

          this.loadDatasetFieldSelections();

          this.orgBusinessUnitsApiService
            .getOrgBusinessUnitsForUser(this.userContextService.getUserContext().userProfileUId)
            .subscribe(businessUnits => {
              this.processBusinessUnits(businessUnits);
            });
        }

        this.isActive = isActive;
      });

    this.store
      .select(fromComm.selectDatasetsForSelect)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ds => {
        this.datasets = ds;

        if (this.distributionList && this.datasets?.length > 0) {
          const firstDataset = this.datasets[0];
          this.distributionList.queryMatchRule.datasetUId = firstDataset.uId;
        }

        this.loadDatasetFieldSelections();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  tabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  listTypeChange(): void {
    this.distributionList.channelType = this.selectedChannelType.channelType;
    this.distributionList.channelUId = this.selectedChannelType.channelUId;

    this.loadDatasetFieldSelections();
  }

  datasetChange(): void {
    this.loadDatasetFieldSelections();
  }

  loadDatasetFieldSelections(): void {
    const datasetUId = this.distributionList?.queryMatchRule?.datasetUId;
    if (!datasetUId) {
      return;
    }

    const dataset: Dataset = this.datasets?.find(ds => ds.uId === datasetUId);
    if (!dataset) {
      return;
    }

    const nameFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text);
    this.nameFields = nameFields;

    let destFields: SchemaField[];
    if (this.distributionList.channelType === ChannelType.Email) {
      destFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Email);
    } else if (this.distributionList.channelType === ChannelType.Sms) {
      destFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Phone);
    } else if (this.distributionList.channelType === ChannelType.MassNotifications) {
      destFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text || f.dataType === FieldDataType.Number);
    }

    this.destinationFields = destFields;

    if (nameFields.length > 0) {
      this.distributionList.nameFieldUId = nameFields[0].uId;
    }

    if (destFields.length > 0) {
      this.distributionList.destinationFieldUId = destFields[0].uId;
    }
  }

  close() {
    this.store.dispatch(fromComm.cancelNewDistributionList());
  }

  cancelClick() {
    this.close();
  }

  saveClick() {
    const distributionList = this.distributionList;

    if (distributionList.channelType === ChannelType.MassNotifications) {
      distributionList.nameFieldUId = null;
    }

    if (!distributionList.name) {
      this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-NAME-REQUIRED'));
      return;
    }
    if (!distributionList.channelType) {
      this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-LIST-TYPE-REQUIRED'));
      return;
    }
    if (!distributionList.queryMatchRule.datasetUId || distributionList.queryMatchRule.datasetUId === GUID_EMPTY) {
      this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-DATASET-REQUIRED'));
      return;
    }
    if (!distributionList.nameFieldUId || distributionList.nameFieldUId === GUID_EMPTY) {
      if (distributionList.channelType != ChannelType.MassNotifications) {
        this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-NAME-FIELD-REQUIRED'));
        return;
      }
    }
    if (!distributionList.destinationFieldUId || distributionList.destinationFieldUId === GUID_EMPTY) {
      if (distributionList.channelType === ChannelType.Email) {
        this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-EMAIL-REQUIRED'));
      }
      if (distributionList.channelType === ChannelType.Sms) {
        this.notify.error(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.VALIDATION-PHONE-REQUIRED'));
      }
      return;
    }

    this.store.dispatch(fromComm.saveDistributionListToApi({ distributionList: this.distributionList }));
  }

  public get ChannelType() {
    return ChannelType;
  }

  private processBusinessUnits(businessUnits: OrgBusinessUnit[]): void {
    const rootUnit = businessUnits?.find(u => !!!u.parentUId);
    this.rootUnitUId = null;
    this.selectedUnitUIds = [];
    if (!!rootUnit) {
      rootUnit.name = this.translate.instant('MANAGEMENT.MENU.GLOBAL-UNIT', { name: rootUnit.name });
      this.rootUnitUId = rootUnit.uId;
    }
    if (!!this.distributionList) {
      if (this.distributionList.businessUnitUIds?.length > 0) {
        this.selectedUnitUIds = this.distributionList.businessUnitUIds.sort((c1, c2) =>
          businessUnits.find(b => b.uId == c1)?.level > businessUnits.find(b => b.uId == c2)?.level ? 1 : -1
        );
      }
    }
    this.businessUnits = businessUnits;
  }

  onBusinessUnitsSelectionChanged(selectedUIds: string[]): void {
    this.selectedUnitUIds = selectedUIds;
    this.distributionList.businessUnitUIds = selectedUIds;
  }
}
